import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { RadFolderEnum, RadFolderEnumMapping } from '../../../lib/types/enums/rad/rad-folder.enum'
import { RadInput } from '../../../lib/types/models/rad/rad-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import {
  createDateStringSchema,
  createTimeSchema
} from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const zodSchema: ZodType<NullableTransformer<RadInput>> = z.object({
  typeOfFolder: createEnumSchema({ fieldName: 'type de pli', enumType: RadFolderEnum }),
  title: createStringSchema('titre'),
  date: createDateStringSchema({ inputName: 'Date' }),
  hours: createTimeSchema({ inputName: 'Heure' }),
  description: createStringSchema('description', { nullish: true }),
  addressName: createStringSchema("nom de l'adresse"),
  address1: createStringSchema('adresse'),
  address2: createStringSchema("complément d'adresse", { nullish: true }),
  city: createStringSchema('ville'),
  zipCode: createStringSchema('code postal'),
  country: createStringSchema('pays')
})

const RadForm = ({ defaultValues, ...validationButtonProps }: BaseFormProps<RadInput>) => {
  const form = useForm<RadInput>({
    resolver: zodResolver(zodSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-4'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Détails
          </Typography>

          <div className="flex gap-2 justify-between">
            <FormField
              control={form.control}
              name="typeOfFolder"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      searchable
                      label="Type de pli"
                      options={Object.values(RadFolderEnum).map((option) => ({
                        label: RadFolderEnumMapping[option],
                        value: option
                      }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        RadFolderEnumMapping[field.value]
                      )}
                      onChange={(option) => field.onChange(option)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput required type="text" label="Titre" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-2 justify-between">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      required
                      label={'Sélectionner la date'}
                      iconIsRight
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hours"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput
                      required
                      {...field}
                      label="Sélectionner l'heure"
                      wrapperClassName="flex-1"
                      value={field.value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="textarea" placeholder="Description..." />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>

        <div className="h-4" />

        <InnerBox className="p-4 rounded-xl">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            Adresse
          </Typography>
          <div className="h-4" />
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="addressName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="Nom de l'adresse" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address1"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledAdressInput
                      {...field}
                      required
                      label="Adresse"
                      onDetailsChange={(placeDetails) => {
                        form.setValue('address1', placeDetails.address, { shouldValidate: true })
                        form.setValue('zipCode', placeDetails.postalCode, {
                          shouldValidate: true
                        })
                        form.setValue('city', placeDetails.city, { shouldValidate: true })
                        form.setValue('country', placeDetails.country, { shouldValidate: true })
                        form.trigger(['address1', 'zipCode', 'city', 'country'])
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address2"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} type="text" label="Complément d'adresse" />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-3">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required type="text" label="Ville" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required type="text" label="Code postal" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="Pays" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default RadForm
